import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { VLLMChatModelTypeArr } from '@shared/index';
import { aiTextInputSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { EventManagerService } from '../../event-manager/event-manager.service';

/** Chat-Completions über selbstgehostete vLLM-Modelle (`RAGAI.AITEXT_VLLM`). */
/**
 * Chat-Completions über selbstgehostete vLLM-Modelle (`RAGAI.AITEXT_VLLM`).
 * @remarks
 * Strukturell identisch zu {@link OpenaiService} (gleicher Backend-Endpunkt-Namensraum
 * `/api/toolbox/completion/create/<llmType>/<botId>`), aber ohne Modell-/Temperatur-Auswahl - vLLM verwendet
 * immer das erste konfigurierte Modell aus `VLLMChatModelTypeArr`, da selbstgehostete Modelle typischerweise
 * nicht pro Anfrage gewechselt werden.
 */
@Injectable({ providedIn: 'root' })
export class VllmService {
  constructor(
    private eventManager: EventManagerService,
    private http: HttpClient,
    private botIdService: BotidService) { }

  private async _complexCompletion(modelConf: { model: string }, promptInput: string, llmType: string, jsonResponse: boolean) {
    this.eventManager.triggerEventHook("io_out_chatcompletion_start", { modelConf, promptInput, llmType, jsonResponse });
    const result = await firstValueFrom(this.http.post("/api/toolbox/completion/create/" + llmType + "/" + this.botIdService.getBotId(),
      {
        modelConf,
        promptInput,
        jsonResponse
      }
    ));
    this.eventManager.triggerEventHook("io_in_chatcompletion_end", { modelConf, promptInput, llmType, jsonResponse, result });
    return result;
  }

  /**
   * Sendet eine Chat-Completion-Anfrage an das vLLM-Modell und liefert die Text-Antwort.
   * @param input Der Prompt-Text oder ein Objekt gemäß `aiTextInputSchema`.
   * @returns Die Antwort des Modells.
   */
  public async chatCompletion(input: string | z.input<typeof aiTextInputSchema>) {
    const parsed = aiTextInputSchema.safeParse(input);
    if (parsed.success && typeof input === 'object') {
      input = parsed.data.input;
    }
    const modelConf = {
      model: VLLMChatModelTypeArr[0]
    };
    const promptInput = input as string;
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_VLLM.CHATCOMPLETION.request", { request: { modelConf, promptInput } });
    const result = await this._complexCompletion(modelConf, promptInput, "vllm", false);
    this.eventManager._trigger_RAGAI_EVENT("RAGAI.AITEXT_VLLM.CHATCOMPLETION.result", { result, request: { modelConf, promptInput } });
    return result;
  }

  /**
   * Wie {@link chatCompletion}, fordert aber eine JSON-Antwort vom Modell an.
   * @param input Der Prompt-Text oder ein Objekt gemäß `aiTextInputSchema`.
   * @returns Die Antwort des Modells als JSON-String.
   */
  public async chatCompletionJson(input: string | z.input<typeof aiTextInputSchema>) {
    const parsed = aiTextInputSchema.safeParse(input);
    if (parsed.success && typeof input === 'object') {
      input = parsed.data.input;
    }
    const modelConf = {
      model: VLLMChatModelTypeArr[0]
    };
    const promptInput = input as string;
    return this._complexCompletion(modelConf, promptInput, "vllm", true);
  }
}
